/**
 * API Configuration for SaaS Dashboard
 * Handles environment-based URL configuration and safe defaults
 */

/**
 * Get API base URL from environment or default
 * @returns {string} API base URL
 */
export function getAPIBaseURL() {
  // Check environment variable first
  // CRITICAL: Use .trim() to remove trailing newlines from Vercel env vars
  if (import.meta.env.MODE === 'test' || import.meta.env.VITEST || (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    return 'http://localhost:8000';
  }

  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL.trim();
  }
  
  // Development default
  if (import.meta.env.DEV) {
    return 'http://localhost:8000';
  }
  
  // SECURITY: Production default uses HTTPS to encrypt data in transit
  // Standard ecosystem gateway (NO AUTH): /api/quantrolix/*
  return 'https://apps.quantrolix.ai/api/quantrolix';
}

/**
 * Get API timeout from environment or default
 * @returns {number} Timeout in milliseconds
 */
export function getAPITimeout() {
  const timeout = parseInt(import.meta.env.VITE_API_TIMEOUT, 10);
  return isNaN(timeout) ? 10000 : timeout; // 10 second default
}

/**
 * Check if feature is enabled
 * @param {string} featureName - Feature environment variable name
 * @param {boolean} defaultValue - Default if not set
 * @returns {boolean} Feature enabled status
 */
export function isFeatureEnabled(featureName, defaultValue = false) {
  const envValue = import.meta.env[featureName];
  
  if (envValue === undefined || envValue === null) {
    return defaultValue;
  }
  
  if (typeof envValue === 'boolean') {
    return envValue;
  }
  
  if (typeof envValue === 'string') {
    return envValue.toLowerCase() === 'true';
  }
  
  return Boolean(envValue);
}

/**
 * Get debug setting
 * @param {string} debugName - Debug environment variable name
 * @returns {boolean} Debug enabled status
 */
export function isDebugEnabled(debugName) {
  return isFeatureEnabled(debugName, false);
}

/**
 * API Configuration object
 */
export const API_CONFIG = {
  baseURL: getAPIBaseURL(),
  timeout: getAPITimeout(),
  endpoints: {
    dashboard: '/api/dashboard/stats', // Align with backend stats endpoint
    products: {
      search: '/api/products/search', // Public product search (no auth); search-public was never deployed
      topPerforming: '/api/dashboard/stats', // Get top products from dashboard
      save: '/api/products/save',
      barcodeScan: '/api/products/barcode-scan',
    },
    agents: {
      status: '/api/agents/status',
    },
    scrapers: '/scrapers',
    websocket: '/api/notifications/subscribe',
  },
  features: {
    websockets: isFeatureEnabled('VITE_ENABLE_WEBSOCKETS', true),
    caching: isFeatureEnabled('VITE_ENABLE_CACHING', true),
    offlineMode: isFeatureEnabled('VITE_ENABLE_OFFLINE_MODE', false),
    liveData: isFeatureEnabled('VITE_USE_LIVE_DATA', false),
    marketplaceSearch: isFeatureEnabled('VITE_ENABLE_MARKETPLACE_SEARCH', true),
    barcodeScan: isFeatureEnabled('VITE_ENABLE_BARCODE_SCAN', true),
    fallbackToMock: isFeatureEnabled('VITE_FALLBACK_TO_MOCK', false),
  },
  debug: {
    api: isDebugEnabled('VITE_DEBUG_API'),
    store: isDebugEnabled('VITE_DEBUG_STORE'),
  },
};

/**
 * Build full URL for endpoint
 * @param {string} endpoint - API endpoint
 * @returns {string} Full URL
 */
export function buildURL(endpoint) {
  if (endpoint.startsWith('http')) {
    return endpoint;
  }
  
  const baseURL = API_CONFIG.baseURL.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  return `${baseURL}${cleanEndpoint}`;
}

/**
 * Get WebSocket URL
 * @param {string} endpoint - WebSocket endpoint
 * @returns {string} WebSocket URL
 */
export function getWebSocketURL(endpoint = API_CONFIG.endpoints.websocket) {
  const httpURL = buildURL(endpoint);
  // Properly convert HTTPS to WSS for security compliance
  return httpURL.replace(/^https/, 'wss').replace(/^http/, 'ws');
}
